import React, { useState, useEffect } from 'react';
import { Upload, FileText, Download, CheckCircle, XCircle, AlertCircle, Loader2, RefreshCw, Search, Eye, X } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const WorkOrderManagement = () => {
  const [workOrders, setWorkOrders] = useState([]);
  const [filteredWorkOrders, setFilteredWorkOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [filterSignature, setFilterSignature] = useState('all');
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [showPdfModal, setShowPdfModal] = useState(false);

  useEffect(() => {
    fetchWorkOrders();
  }, []);

  useEffect(() => {
    filterWorkOrders();
  }, [searchTerm, workOrders, filterSignature]);

  const fetchWorkOrders = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/workorders/initial`);
      if (!response.ok) throw new Error('Failed to fetch work orders');
      const data = await response.json();
      setWorkOrders(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const filterWorkOrders = () => {
    let filtered = workOrders;

    if (searchTerm) {
      filtered = filtered.filter(wo =>
        wo.manual_wo_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        wo.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        wo.vehicle_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        wo.location?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterSignature === 'complete') {
      filtered = filtered.filter(wo => wo.driver_signature && wo.customer_signature);
    } else if (filterSignature === 'incomplete') {
      filtered = filtered.filter(wo => !wo.driver_signature || !wo.customer_signature);
    }

    setFilteredWorkOrders(filtered);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setSelectedFile(file);
      setUploadResult(null);
      setError(null);
      
      // Create preview URL
      const url = URL.createObjectURL(file);
      setPdfPreviewUrl(url);
    } else {
      setError('Please select a PDF file');
      setSelectedFile(null);
      setPdfPreviewUrl(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file first');
      return;
    }

    setUploading(true);
    setError(null);
    setUploadResult(null);

    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const response = await fetch(`${API_BASE_URL}/upload`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Upload failed');
      }
      
      const data = await response.json();
      setUploadResult(data);
      
      // Keep PDF preview visible with results
      setTimeout(() => {
        fetchWorkOrders();
      }, 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const clearUpload = () => {
    setSelectedFile(null);
    setUploadResult(null);
    setPdfPreviewUrl(null);
    const fileInput = document.getElementById('file-upload');
    if (fileInput) fileInput.value = '';
  };

  const handleExport = async () => {
    setExporting(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/export/excel`);
      if (!response.ok) throw new Error('Export failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `workorders_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      try {
        const response = await fetch(`${API_BASE_URL}/export/excel`);
        const data = await response.json();
        
        if (data.file_path) {
          const link = document.createElement('a');
          link.href = `${API_BASE_URL}/export/excel`;
          link.download = 'workorders.xlsx';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      } catch (secondErr) {
        setError('Export failed: ' + secondErr.message);
      }
    } finally {
      setExporting(false);
    }
  };

  const getConfidenceBadge = (confidence) => {
    const configs = {
      high: { color: 'bg-green-100 text-green-800 border-green-300', label: 'High' },
      medium: { color: 'bg-yellow-100 text-yellow-800 border-yellow-300', label: 'Med' },
      low: { color: 'bg-red-100 text-red-800 border-red-300', label: 'Low' }
    };
    const config = configs[confidence] || configs.low;
    return (
      <span className={`text-xs px-2 py-0.5 rounded border ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const WorkOrderDetailModal = ({ workOrder, onClose }) => {
    if (!workOrder) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
        <div className="bg-white rounded-lg shadow-2xl max-w-6xl w-full max-h-[90vh] flex flex-col md:flex-row overflow-hidden" onClick={(e) => e.stopPropagation()}>
          {/* Details Section */}
          <div className="flex-1 overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h3 className="text-2xl font-bold text-gray-800">Work Order Details</h3>
              <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                <X size={24} />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <label className="text-xs font-semibold text-gray-500 uppercase">WO Number</label>
                  <div className="flex items-center gap-2 mt-1">
                    <p className="text-lg font-bold text-gray-900">{workOrder.manual_wo_number}</p>
                    {getConfidenceBadge(workOrder.confidence_manual_wo_number)}
                  </div>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <label className="text-xs font-semibold text-gray-500 uppercase">Date</label>
                  <div className="flex items-center gap-2 mt-1">
                    <p className="text-lg font-semibold text-gray-900">{workOrder.date}</p>
                    {getConfidenceBadge(workOrder.confidence_date)}
                  </div>
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase">Customer Name</label>
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-lg text-gray-900">{workOrder.customer_name}</p>
                  {getConfidenceBadge(workOrder.confidence_customer_name)}
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase">Location</label>
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-lg text-gray-900">{workOrder.location}</p>
                  {getConfidenceBadge(workOrder.confidence_location)}
                </div>
              </div>

              <div className="bg-indigo-50 p-4 rounded-lg">
                <label className="text-xs font-semibold text-indigo-700 uppercase">Vehicle Number</label>
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-xl font-mono font-bold text-indigo-900">{workOrder.vehicle_no}</p>
                  {getConfidenceBadge(workOrder.confidence_vehicle_no)}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase">Arrival Time</label>
                  <div className="flex items-center gap-2 mt-1">
                    <p className="text-lg text-gray-900">{workOrder.arrival_time}</p>
                    {getConfidenceBadge(workOrder.confidence_arrival_time)}
                  </div>
                </div>
                
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase">Departure Time</label>
                  <div className="flex items-center gap-2 mt-1">
                    <p className="text-lg text-gray-900">{workOrder.departure_time}</p>
                    {getConfidenceBadge(workOrder.confidence_departure_time)}
                  </div>
                </div>
              </div>

              <div className="bg-green-50 p-4 rounded-lg">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold text-green-700 uppercase">Gallons</label>
                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-2xl font-bold text-green-900">{workOrder.gallons}</p>
                      {getConfidenceBadge(workOrder.confidence_gallons)}
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-xs font-semibold text-green-700 uppercase">Number of Loads</label>
                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-2xl font-bold text-green-900">{workOrder.no_of_loads}</p>
                      {getConfidenceBadge(workOrder.confidence_no_of_loads)}
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase">Description of Work</label>
                <div className="flex items-start gap-2 mt-1">
                  <p className="text-gray-900">{workOrder.description_of_work}</p>
                  {getConfidenceBadge(workOrder.confidence_description_of_work)}
                </div>
              </div>

              {workOrder.remarks && (
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase">Remarks</label>
                  <div className="flex items-start gap-2 mt-1">
                    <p className="text-gray-900">{workOrder.remarks}</p>
                    {getConfidenceBadge(workOrder.confidence_remarks)}
                  </div>
                </div>
              )}

              <div className="border-t pt-4">
                <label className="text-xs font-semibold text-gray-500 uppercase mb-3 block">Signatures</label>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    {workOrder.driver_signature ? (
                      <CheckCircle className="text-green-500" size={24} />
                    ) : (
                      <XCircle className="text-red-500" size={24} />
                    )}
                    <div>
                      <p className="font-semibold text-gray-900">Driver Signature</p>
                      <p className="text-sm text-gray-600">
                        {workOrder.driver_signature ? 'Signed' : 'Not Signed'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    {workOrder.customer_signature ? (
                      <CheckCircle className="text-green-500" size={24} />
                    ) : (
                      <XCircle className="text-red-500" size={24} />
                    )}
                    <div>
                      <p className="font-semibold text-gray-900">Customer Signature</p>
                      <p className="text-sm text-gray-600">
                        {workOrder.customer_signature ? 'Signed' : 'Not Signed'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="text-sm text-gray-500 border-t pt-4">
                <p>Source File: {workOrder.file_path}</p>
                <p>Status: {workOrder.status}</p>
              </div>
            </div>
          </div>

          {/* PDF Viewer Section */}
          {workOrder.file_path && (
            <div className="w-full md:w-1/2 bg-gray-100 border-l flex flex-col">
              <div className="bg-gray-800 text-white px-6 py-4">
                <h3 className="font-semibold">PDF Document</h3>
              </div>
              <div className="flex-1 overflow-hidden">
                <iframe
                  src={`${API_BASE_URL}/pdf/${encodeURIComponent(workOrder.file_path)}`}
                  className="w-full h-full"
                  title="Work Order PDF"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">Work Order Management</h1>
          <p className="text-gray-600">AI-powered PDF extraction and management system</p>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-lg shadow animate-shake">
            <div className="flex items-start">
              <AlertCircle className="text-red-500 mr-3 flex-shrink-0 mt-0.5" size={20} />
              <div className="flex-1">
                <p className="text-red-700 font-semibold">Error</p>
                <p className="text-red-600 text-sm">{error}</p>
              </div>
              <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700 ml-2">
                <X size={18} />
              </button>
            </div>
          </div>
        )}

        {/* Upload Section with PDF Preview */}
        {(selectedFile || uploadResult) && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-gray-800">
                {uploading ? 'Processing Document...' : uploadResult ? 'Extraction Complete' : 'Document Preview'}
              </h2>
              <button
                onClick={clearUpload}
                className="text-gray-500 hover:text-gray-700"
                disabled={uploading}
              >
                <X size={24} />
              </button>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              {/* PDF Preview */}
              <div className="bg-gray-100 rounded-lg overflow-hidden" style={{ height: '600px' }}>
                <div className="bg-gray-800 text-white px-4 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText size={20} />
                    <span className="font-medium">{selectedFile?.name}</span>
                  </div>
                </div>
                <div className="h-full bg-gray-200">
                  {pdfPreviewUrl && (
                    <iframe
                      src={pdfPreviewUrl}
                      className="w-full h-full"
                      title="PDF Preview"
                    />
                  )}
                </div>
              </div>

              {/* Results/Loading Section */}
              <div className="flex flex-col justify-center">
                {uploading ? (
                  <div className="flex flex-col items-center justify-center py-16">
                    <Loader2 className="animate-spin text-indigo-500 mb-4" size={64} />
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">Processing PDF...</h3>
                    <p className="text-gray-600 text-center">
                      Extracting work order information using AI...
                    </p>
                    <div className="mt-6 w-full max-w-md">
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full bg-indigo-500 animate-pulse" style={{ width: '70%' }}></div>
                      </div>
                    </div>
                  </div>
                ) : uploadResult ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 mb-6">
                      <CheckCircle className="text-green-500" size={48} />
                      <div>
                        <h3 className="text-xl font-bold text-gray-800">Extraction Successful!</h3>
                        <p className="text-gray-600">Work order data extracted and saved</p>
                      </div>
                    </div>
                    
                    <div className="bg-gray-50 rounded-lg p-4 space-y-4 max-h-[480px] overflow-y-auto">
                      {/* {uploadResult.data.map((item, idx) => ( */}
                      {(uploadResult?.results || []).map((item, idx) =>(
                        <div key={idx} className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                              <span className="font-semibold text-gray-600">WO Number:</span>
                              <p className="text-gray-900 font-mono">{item.ManualWONumber}</p>
                            </div>
                            <div>
                              <span className="font-semibold text-gray-600">Date:</span>
                              <p className="text-gray-900">{item.Date}</p>
                            </div>
                            <div className="col-span-2">
                              <span className="font-semibold text-gray-600">Customer:</span>
                              <p className="text-gray-900">{item.CustomerName}</p>
                            </div>
                            <div>
                              <span className="font-semibold text-gray-600">Vehicle:</span>
                              <p className="text-gray-900 font-mono">{item.VehicleNo}</p>
                            </div>
                            <div>
                              <span className="font-semibold text-gray-600">Gallons:</span>
                              <p className="text-gray-900 font-bold text-green-600">{item.Gallons}</p>
                            </div>
                            <div className="col-span-2">
                              <span className="font-semibold text-gray-600">Location:</span>
                              <p className="text-gray-900">{item.Location}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="bg-indigo-100 p-6 rounded-full inline-block mb-4">
                      <Upload className="text-indigo-600" size={48} />
                    </div>
                    <h3 className="text-xl font-semibold mb-2">Ready to Upload</h3>
                    <p className="text-gray-600 mb-6">
                      Click the button below to extract data from this PDF
                    </p>
                    <button
                      onClick={handleUpload}
                      className="bg-green-500 text-white px-8 py-3 rounded-lg hover:bg-green-600 transition-all shadow-md hover:shadow-lg flex items-center font-semibold mx-auto"
                    >
                      <Upload className="mr-2" size={18} />
                      Upload & Extract
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Actions Panel - Only show if no file selected */}
        {!selectedFile && !uploadResult && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Upload Section */}
              <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 hover:border-indigo-500 hover:bg-indigo-50 transition-all">
                <div className="flex flex-col items-center">
                  <div className="bg-indigo-100 p-4 rounded-full mb-4">
                    <Upload className="text-indigo-600" size={32} />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Upload Work Order</h3>
                  <p className="text-sm text-gray-500 mb-4 text-center">Upload PDF files for AI extraction</p>
                  
                  <input
                    type="file"
                    accept="application/pdf"
                    onChange={handleFileSelect}
                    className="hidden"
                    id="file-upload"
                  />
                  
                  <label
                    htmlFor="file-upload"
                    className="cursor-pointer bg-indigo-500 text-white px-6 py-3 rounded-lg hover:bg-indigo-600 transition-all shadow-md hover:shadow-lg"
                  >
                    Select PDF File
                  </label>
                </div>
              </div>

              {/* Export Section */}
              <div className="border-2 border-gray-300 rounded-xl p-6 hover:bg-green-50 transition-all">
                <div className="flex flex-col items-center justify-center h-full">
                  <div className="bg-green-100 p-4 rounded-full mb-4">
                    <Download className="text-green-600" size={32} />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Export to Excel</h3>
                  <p className="text-sm text-gray-500 mb-4 text-center">
                    Download all work orders as Excel spreadsheet
                  </p>
                  <p className="text-lg font-bold text-gray-700 mb-4">
                    {workOrders.length} work order{workOrders.length !== 1 ? 's' : ''} available
                  </p>
                  <button
                    onClick={handleExport}
                    disabled={exporting || workOrders.length === 0}
                    className="bg-green-500 text-white px-8 py-3 rounded-lg hover:bg-green-600 transition-all shadow-md hover:shadow-lg disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center font-semibold"
                  >
                    {exporting ? (
                      <>
                        <Loader2 className="animate-spin mr-2" size={18} />
                        Exporting...
                      </>
                    ) : (
                      <>
                        <Download className="mr-2" size={18} />
                        Export Excel
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Work Orders List */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">
                Work Orders ({filteredWorkOrders.length})
              </h2>
              {searchTerm && (
                <p className="text-sm text-gray-500 mt-1">
                  Filtered from {workOrders.length} total
                </p>
              )}
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  placeholder="Search work orders..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 w-full sm:w-64"
                />
              </div>

              <select
                value={filterSignature}
                onChange={(e) => setFilterSignature(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="all">All Signatures</option>
                <option value="complete">Complete</option>
                <option value="incomplete">Incomplete</option>
              </select>
              
              <button
                onClick={fetchWorkOrders}
                disabled={loading}
                className="bg-indigo-500 text-white px-4 py-2 rounded-lg hover:bg-indigo-600 transition-all shadow hover:shadow-md flex items-center justify-center"
              >
                <RefreshCw className={`mr-2 ${loading ? 'animate-spin' : ''}`} size={18} />
                Refresh
              </button>
            </div>
          </div>

          {loading ? (
            <div className="flex flex-col justify-center items-center py-16">
              <Loader2 className="animate-spin text-indigo-500 mb-4" size={48} />
              <p className="text-gray-500">Loading work orders...</p>
            </div>
          ) : filteredWorkOrders.length === 0 ? (
            <div className="text-center py-16 text-gray-500">
              <FileText size={64} className="mx-auto mb-4 opacity-30" />
              <p className="text-lg font-semibold mb-2">
                {workOrders.length === 0 ? 'No work orders found' : 'No matching work orders'}
              </p>
              <p className="text-sm">
                {workOrders.length === 0 
                  ? 'Upload a PDF to get started' 
                  : 'Try adjusting your search or filters'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">WO #</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Customer</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Vehicle</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Location</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Gallons</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Signatures</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredWorkOrders.map((wo) => (
                    <tr key={wo.id} className="hover:bg-indigo-50 transition-colors">
                      <td className="px-4 py-4">
                        <div className="font-bold text-gray-900">{wo.manual_wo_number}</div>
                        <div className="mt-1">{getConfidenceBadge(wo.confidence_manual_wo_number)}</div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-gray-900 max-w-xs truncate">{wo.customer_name}</div>
                        <div className="mt-1">{getConfidenceBadge(wo.confidence_customer_name)}</div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="font-mono font-semibold text-gray-900">{wo.vehicle_no}</div>
                        <div className="mt-1">{getConfidenceBadge(wo.confidence_vehicle_no)}</div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-gray-900 max-w-xs truncate">{wo.location}</div>
                      </td>
                      <td className="px-4 py-4 text-gray-900 whitespace-nowrap">{wo.date}</td>
                      <td className="px-4 py-4">
                        <div className="font-bold text-gray-900">{wo.gallons}</div>
                        <div className="text-xs text-gray-500 mt-1">{wo.no_of_loads} load(s)</div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex gap-2">
                          {wo.driver_signature ? (
                            <div className="flex items-center gap-1 bg-green-50 px-2 py-1 rounded" title="Driver Signed">
                              <CheckCircle className="text-green-500" size={18} />
                              <span className="text-xs text-green-700">Driver</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1 bg-red-50 px-2 py-1 rounded" title="Driver Not Signed">
                              <XCircle className="text-red-500" size={18} />
                              <span className="text-xs text-red-700">Driver</span>
                            </div>
                          )}
                          {wo.customer_signature ? (
                            <div className="flex items-center gap-1 bg-green-50 px-2 py-1 rounded" title="Customer Signed">
                              <CheckCircle className="text-green-500" size={18} />
                              <span className="text-xs text-green-700">Customer</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1 bg-red-50 px-2 py-1 rounded" title="Customer Not Signed">
                              <XCircle className="text-red-500" size={18} />
                              <span className="text-xs text-red-700">Customer</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <button
                          onClick={() => setSelectedWorkOrder(wo)}
                          className="flex items-center gap-2 bg-indigo-500 text-white px-3 py-2 rounded-lg hover:bg-indigo-600 transition-all text-sm font-medium shadow hover:shadow-md"
                        >
                          <Eye size={16} />
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Stats Footer */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500 mb-1">Total Orders</div>
            <div className="text-2xl font-bold text-gray-900">{workOrders.length}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500 mb-1">Complete Signatures</div>
            <div className="text-2xl font-bold text-green-600">
              {workOrders.filter(wo => wo.driver_signature && wo.customer_signature).length}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500 mb-1">Incomplete Signatures</div>
            <div className="text-2xl font-bold text-red-600">
              {workOrders.filter(wo => !wo.driver_signature || !wo.customer_signature).length}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500 mb-1">Total Gallons</div>
            <div className="text-2xl font-bold text-indigo-600">
              {workOrders.reduce((sum, wo) => sum + (parseInt(wo.gallons) || 0), 0).toLocaleString()}
            </div>
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      {selectedWorkOrder && (
        <WorkOrderDetailModal
          workOrder={selectedWorkOrder}
          onClose={() => setSelectedWorkOrder(null)}
        />
      )}
    </div>
  );
};

export default WorkOrderManagement;